/**
 * Archies — gender gallery filter (native Horizon slideshow).
 *
 * Slides are tagged server-side with `media-gender--{women|men|shared}` in
 * blocks/_product-media-gallery.liquid (derived from the media alt convention
 * "<Colour> - Women Image" / "<Colour> - Men Image"; empty alt = shared/product shots).
 *
 * The active gender lives on body[data-pdp-gender] (set by tabs-ext.js, survives the
 * <main> morph on a combined-listing colour swap). When it changes — or after a colour
 * swap re-renders the gallery — we set Horizon's own slideshow-slide[hidden] attribute on
 * the non-active gender's slides. slideshow.js's `get slides()` excludes [hidden] slides,
 * so the native slideshow filters itself (no slide-list rebuild, no stale cache). Shared
 * (product-on-white) slides always show. We then nudge the slideshow onto the first visible
 * slide via the [initial-slide] attribute — the same hook Horizon uses for collection filtering.
 */
(function () {
  'use strict';

  var GALLERY_SEL = 'media-gallery, .product-information__media';

  function activeGender() {
    return document.body.getAttribute('data-pdp-gender') === 'men' ? 'men' : 'women';
  }

  // Sets slideshow-slide[hidden] on the non-active gender's slides (+ matching pagination controls).
  // Pure DOM attribute work — safe to call on a DETACHED gallery before it's inserted, which is how
  // media-gallery.js pre-filters the incoming gallery so it mounts in its final layout (no post-mount
  // relayout → no slide-0 flash). Returns the first visible slide element (or null).
  function filterSlides(gallery) {
    var slides = gallery.querySelectorAll('slideshow-slide');
    if (!slides.length) return null;

    var gender = activeGender();
    var other = gender === 'men' ? 'women' : 'men';
    var otherClass = 'media-gender--' + other;

    var hiddenIds = {};
    var firstVisible = null;

    slides.forEach(function (slide) {
      if (slide.classList.contains(otherClass)) {
        slide.setAttribute('hidden', '');
        slide.removeAttribute('reveal');
        var sid = slide.getAttribute('slide-id');
        if (sid) hiddenIds[sid] = true;
      } else {
        slide.removeAttribute('hidden');
        slide.removeAttribute('reveal');
        if (!firstVisible) firstVisible = slide;
      }
    });

    // Pagination dots / thumbnails (present on carousel presentations) are rendered one-per-media
    // server-side. Hide any control whose slide-id points at a now-hidden slide so the count matches.
    gallery.querySelectorAll('[slide-id]').forEach(function (ctrl) {
      if (ctrl.tagName.toLowerCase() === 'slideshow-slide') return;
      var sid = ctrl.getAttribute('slide-id');
      if (!sid) return;
      ctrl.toggleAttribute('hidden', !!hiddenIds[sid]);
    });

    // Desktop 'thumbnails' pagination renders one thumbnail per media but carries NO slide-id, so the
    // pass above can't reach it — which is why the thumbnail rail showed both genders on desktop while
    // the main slides filtered correctly. Tag each thumbnail from its own <img alt> using the same
    // convention as the slides ("<Colour> - Women/Men Image"; product-title/empty = shared, always
    // shown). Check 'Women' before 'Men' because "Women" contains the substring "men".
    gallery.querySelectorAll('.slideshow-controls__thumbnail').forEach(function (thumb) {
      var img = thumb.querySelector('img');
      var alt = img ? (img.getAttribute('alt') || '') : '';
      var thumbGender = 'shared';
      if (alt.indexOf('Women') !== -1) {
        thumbGender = 'women';
      } else if (alt.indexOf('Men') !== -1) {
        thumbGender = 'men';
      }
      thumb.toggleAttribute('hidden', thumbGender === other);
    });

    return firstVisible;
  }

  // Filter, then nudge the slideshow to the first visible slide. Used when the active set genuinely
  // changes (gender toggle, colour swap, initial load) and resetting to the first image is correct.
  function applyToGallery(gallery) {
    var firstVisible = filterSlides(gallery);
    var slideshow = gallery.querySelector('slideshow-component');
    if (slideshow && firstVisible && typeof slideshow.select === 'function') {
      var sid = firstVisible.getAttribute('slide-id');
      if (sid) {
        try { slideshow.select({ id: sid }, undefined, { animate: false }); } catch (e) { /* best effort */ }
      }
    }
  }

  function run() {
    document.querySelectorAll(GALLERY_SEL).forEach(applyToGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Re-filter + jump to the first visible slide when the gender toggle flips body[data-pdp-gender].
  new MutationObserver(run).observe(document.body, {
    attributes: true,
    attributeFilter: ['data-pdp-gender'],
  });

  // Size change: media-gallery.js leaves the (identical-media) gallery in place and the gender is
  // unchanged, so the filter + carousel position are already correct — do nothing (re-touching the
  // slides here only risks a reflow that resets the slideshow scroll). [test: no-op]

  // Colour / connected-product swaps morph <main> (no custom flag). Re-apply after the morph settles.
  // [Archies] Guard: size radios ALSO carry data-connected-product-url, but EMPTY — only a real colour
  // swap (non-empty URL → a different product) should re-filter and reset the gallery to the first
  // visible slide. Without this, a same-product SIZE change fired run() at 600ms/1400ms and jumped the
  // carousel to slide 0 (the "gallery size-flash" bug). Gender is unchanged on a size change, so nothing
  // needs re-filtering. See UPGRADE.md.
  document.addEventListener('click', function (e) {
    var swatch = e.target.closest && e.target.closest('[data-connected-product-url]');
    if (!swatch) return;
    if (!swatch.getAttribute('data-connected-product-url')) return;
    setTimeout(run, 600);
    setTimeout(run, 1400);
  }, true);
})();
